import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { CloudUpload, Close, InsertDriveFile } from '@mui/icons-material';
import {
  StyledFileUpload,
  DropZone,
  FileList,
  FileItem,
  UploadIcon,
  UploadText,
  HiddenInput,
  RemoveButton,
  ErrorText,
} from './CustomFileUpload.styles';

/**
 * CustomFileUpload Component
 * 
 * A file upload component with drag-drop support, preview, and validation.
 * 
 * @component
 * @example
 * ```jsx
 * <CustomFileUpload
 *   onChange={(files) => setFiles(files)}
 *   accept="image/*"
 *   maxSize={5}
 *   multiple
 * />
 * ```
 */
const CustomFileUpload = ({
  onChange,
  accept,
  multiple = false,
  maxSize = 10, // in MB
  maxFiles,
  disabled = false,
  error = false,
  helperText,
  showPreview = true,
  className,
  ...props
}) => {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef(null);

  const validateFile = (file) => {
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      return `File ${file.name} exceeds maximum size of ${maxSize}MB`;
    }

    // Check file count
    if (maxFiles && files.length >= maxFiles) {
      return `Maximum ${maxFiles} file(s) allowed`;
    }

    return null;
  };

  const handleFiles = (newFiles) => {
    setErrorMessage('');
    const fileArray = Array.from(newFiles);
    
    // Validate each file
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        setErrorMessage(error);
        return;
      }
    }

    // Update files
    const updatedFiles = multiple ? [...files, ...fileArray] : fileArray;
    
    // Check max files limit
    if (maxFiles && updatedFiles.length > maxFiles) {
      setErrorMessage(`Maximum ${maxFiles} file(s) allowed`);
      return;
    }

    setFiles(updatedFiles);
    onChange?.(updatedFiles);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    if (disabled) return;
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onChange?.(updatedFiles);
  };

  const handleButtonClick = () => {
    inputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <StyledFileUpload $error={error || !!errorMessage} className={className}>
      <DropZone
        $dragActive={dragActive}
        $disabled={disabled}
        $error={error || !!errorMessage}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        <HiddenInput
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          disabled={disabled}
          {...props}
        />
        <UploadIcon>
          <CloudUpload fontSize="large" />
        </UploadIcon>
        <UploadText>
          <strong>Click to upload</strong> or drag and drop
        </UploadText>
        <UploadText $secondary>
          {accept || 'Any file type'} (max {maxSize}MB)
          {maxFiles && ` • Up to ${maxFiles} file${maxFiles > 1 ? 's' : ''}`}
        </UploadText>
      </DropZone>

      {(errorMessage || helperText) && (
        <ErrorText $error={!!errorMessage}>
          {errorMessage || helperText}
        </ErrorText>
      )}

      {showPreview && files.length > 0 && (
        <FileList>
          {files.map((file, index) => (
            <FileItem key={index}>
              <InsertDriveFile fontSize="small" />
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-size">{formatFileSize(file.size)}</div>
              </div>
              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile(index);
                }}
                disabled={disabled}
              >
                <Close fontSize="small" />
              </RemoveButton>
            </FileItem>
          ))}
        </FileList>
      )}
    </StyledFileUpload>
  );
};

CustomFileUpload.propTypes = {
  /** Callback fired when files change */
  onChange: PropTypes.func.isRequired,
  /** Accepted file types (e.g., "image/*", ".pdf,.doc") */
  accept: PropTypes.string,
  /** Allow multiple file selection */
  multiple: PropTypes.bool,
  /** Maximum file size in MB */
  maxSize: PropTypes.number,
  /** Maximum number of files */
  maxFiles: PropTypes.number,
  /** Disable the upload */
  disabled: PropTypes.bool,
  /** Show error state */
  error: PropTypes.bool,
  /** Helper text below the upload area */
  helperText: PropTypes.string,
  /** Show file preview list */
  showPreview: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default CustomFileUpload;
